import { useEffect, useMemo, useRef, useState } from 'react';
import { forfeitMatch, leaveLobby, subscribeToMatches } from '../services/firestore';
import { trackEvent } from '../services/analytics';
import { placementBand, placementLabel, tierLabelForPlacement } from '../utils/placement';
import { useAnonymousUpgradePrompt } from '../hooks/useAnonymousUpgradePrompt';
import Match, { REVEAL_TOTAL_MS } from './Match';
import BracketView from './BracketView';
import AppHeader from './AppHeader';
import AuthModal from './AuthModal';
import './Tournament.css';

// Extra buffer on top of Match's own reveal duration, so this component's
// independently-scheduled timeout never races Match's and cuts the
// animation off a frame early.
const REVEAL_HANDOFF_BUFFER_MS = 200;

// How long the between-round announcement modal stays up before it fades
// out on its own.
const ROUND_MODAL_VISIBLE_MS = 2500;
const ROUND_MODAL_FADE_MS = 300;

function roundLabel(currentRound, totalRounds) {
  const remaining = totalRounds - currentRound + 1;
  if (remaining <= 1) return 'Final';
  if (remaining === 2) return 'Semifinal';
  if (remaining === 3) return 'Quarterfinal';
  return `Round ${currentRound}`;
}

function Tournament({ me, setMe, tournament, connectionError }) {
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    return subscribeToMatches(tournament.id, setMatches, console.error);
  }, [tournament.id]);

  // A player is eliminated once they've lost any match, regardless of round.
  const isEliminated =
    !!me &&
    matches.some(
      (match) =>
        (match.player1Id === me.id || match.player2Id === me.id) &&
        match.status === 'complete' &&
        match.winnerId !== me.id
    );

  const won = !!me && tournament.status === 'completed' && tournament.winnerId === me.id;
  // This player's own run through the bracket is over the moment either of
  // these is true — win, or a loss in any round — regardless of whether the
  // rest of the bracket has finished. Deliberately not tied to
  // tournament.status === 'completed' alone: that only covers the win case,
  // and would otherwise miss an eliminated player who leaves (via
  // handleForfeit's "Return to Lobby") before the other matches wrap up.
  const myParticipationEnded = isEliminated || won;

  // Mirrors myProfile.tournamentsPlayed right up until myParticipationEnded
  // flips true, then stops updating — applyResultToProfile only writes to
  // playerProfiles after tournament.status reaches 'completed', which is
  // always later than that flip, so the frozen value can never reflect
  // points *this* tournament just earned. (Edge case: mounting directly into
  // an already-decided result, e.g. a refresh right after finishing,
  // freezes on whatever myProfile happened to be on first load — acceptable
  // since this prompt is best-effort, not load-bearing.)
  const { authUser, myProfile, isFirstTournamentEligible } = useAnonymousUpgradePrompt({
    freezeEligibility: myParticipationEnded,
  });

  const myPlacementLabel = me ? placementLabel(placementBand(matches, me.id)) : null;
  const myResult = me ? tournament.results?.find((r) => r.playerId === me.id) : null;

  // Fires once per participant per tournament, the moment they're present
  // for the bracket actually finishing (win or loss) — not just "created" or
  // "joined" a lobby that never got played out.
  useEffect(() => {
    if (!me || tournament.status !== 'completed') return;
    trackEvent('complete_tournament', { won: tournament.winnerId === me.id });
  }, [me, tournament.status, tournament.winnerId]);

  const [promptDismissed, setPromptDismissed] = useState(false);
  const showUpgradePrompt =
    myParticipationEnded && !!authUser?.isAnonymous && isFirstTournamentEligible && !promptDismissed;

  // Round advancement (and tournament completion) is handled entirely
  // server-side now — see advanceRoundOnMatchComplete in functions/index.js
  // — so it happens the instant a round's last match completes regardless
  // of whether the host (or anyone) still has the app open. No client-side
  // trigger needed here; this component only ever reads tournament.currentRound
  // reactively off the live subscription.

  const round1Matches = useMemo(() => matches.filter((match) => match.round === 1), [matches]);
  const startingPlayers = round1Matches.reduce((sum, match) => sum + (match.isBye ? 1 : 2), 0);
  const totalRounds = startingPlayers > 1 ? Math.ceil(Math.log2(startingPlayers)) : 1;

  const currentRoundMatches = matches.filter((match) => match.round === tournament.currentRound);
  const playersRemaining = currentRoundMatches.reduce((sum, match) => sum + (match.isBye ? 1 : 2), 0);

  const myMatch = me
    ? matches.find(
        (match) =>
          match.round === tournament.currentRound && (match.player1Id === me.id || match.player2Id === me.id)
      )
    : null;

  // Forfeiting while a match is still live hands the win to the opponent
  // instead of leaving it stuck waiting on someone who's gone; otherwise
  // (already eliminated, or between rounds with no match assigned yet)
  // it's just a plain departure.
  const handleForfeit = () => {
    const activeMatch = myMatch && myMatch.status !== 'complete' ? myMatch : null;
    if (activeMatch) {
      if (!window.confirm('Forfeit this match? Your opponent will win and you will be out of the tournament.')) {
        return;
      }
      forfeitMatch(me.id, tournament.id, activeMatch.id).catch(console.error);
    } else {
      leaveLobby(me.id, tournament.id).catch(console.error);
    }
    setMe(null);
  };

  // A match completing on its deciding game is the one case where its status
  // flips to 'complete' the same instant the round resolves (earlier games
  // in the same match stay 'pending' until the next throw) — and that flip
  // can also be what makes the *tournament* finish, if it was the last
  // match of the last round. Either way, hold the Match view open for the
  // reveal animation's duration before falling through to whichever summary
  // screen comes next, so the deciding game's reveal isn't cut off.
  //
  // The deadline is derived synchronously during render (via a ref), not in
  // an effect: an effect only runs after commit, so the very first render
  // where status flips to 'complete' would still unmount <Match> for one
  // frame before an effect could correct it — and remounting Match resets
  // the ref it uses to detect "did resolvedAt just change", causing it to
  // miss the reveal trigger entirely. A plain rerender-scheduling effect
  // below just wakes the component up once the deadline passes.
  const holdDeadlineRef = useRef({ matchId: null, until: 0 });
  if (myMatch?.status === 'complete' && myMatch.resolvedAt && holdDeadlineRef.current.matchId !== myMatch.id) {
    holdDeadlineRef.current = {
      matchId: myMatch.id,
      until: myMatch.resolvedAt + REVEAL_TOTAL_MS + REVEAL_HANDOFF_BUFFER_MS,
    };
  }

  const [, forceRerender] = useState(0);
  useEffect(() => {
    if (!myMatch || myMatch.status !== 'complete' || holdDeadlineRef.current.matchId !== myMatch.id) return;
    const remaining = holdDeadlineRef.current.until - Date.now();
    if (remaining <= 0) return;
    const timer = setTimeout(() => forceRerender((n) => n + 1), remaining);
    return () => clearTimeout(timer);
  }, [myMatch?.id, myMatch?.status]);

  const holdingMatchForReveal =
    myMatch?.status === 'complete' &&
    holdDeadlineRef.current.matchId === myMatch.id &&
    Date.now() < holdDeadlineRef.current.until;

  // Announce each new tournament round (round number + players remaining)
  // in a brief modal. Skipped for eliminated players — they're not
  // continuing into it. Tracked with a ref (not state) for the "previous"
  // value so this only fires on actual round advances, never on mount.
  const prevRoundRef = useRef(tournament.currentRound);
  const [roundModalPhase, setRoundModalPhase] = useState(null); // null | 'visible' | 'hiding'
  useEffect(() => {
    if (tournament.currentRound === prevRoundRef.current) return;
    prevRoundRef.current = tournament.currentRound;
    if (isEliminated) return;

    setRoundModalPhase('visible');
    const hideTimer = setTimeout(() => setRoundModalPhase('hiding'), ROUND_MODAL_VISIBLE_MS);
    const removeTimer = setTimeout(
      () => setRoundModalPhase(null),
      ROUND_MODAL_VISIBLE_MS + ROUND_MODAL_FADE_MS
    );
    return () => {
      clearTimeout(hideTimer);
      clearTimeout(removeTimer);
    };
  }, [tournament.currentRound, isEliminated]);

  const roundModal = roundModalPhase && (
    <div className={`round-modal-overlay${roundModalPhase === 'hiding' ? ' hiding' : ''}`}>
      <div className="round-modal">
        <div className="round-modal-title">{roundLabel(tournament.currentRound, totalRounds)}</div>
        <div className="round-modal-sub">
          ROUND {tournament.currentRound} / {totalRounds}
        </div>
        <div className="round-modal-players">{playersRemaining} players remaining</div>
      </div>
    </div>
  );

  if (myMatch && (myMatch.status !== 'complete' || holdingMatchForReveal)) {
    return (
      <>
        {/* Keyed by match id so a new round's match mounts a fresh Match
            instance instead of reusing the old one — otherwise its internal
            reveal-animation state (e.g. still mid-"result" from the previous
            match) leaks into the new match's render, showing broken images
            for lastChoice1/2 that don't exist yet on the new match doc. */}
        <Match
          key={myMatch.id}
          match={myMatch}
          me={me}
          roundLabel={roundLabel(tournament.currentRound, totalRounds)}
          totalRounds={totalRounds}
          playersRemaining={playersRemaining}
          onForfeit={handleForfeit}
        />
        {roundModal}
      </>
    );
  }

  if (tournament.status === 'completed') {
    return (
      <div className="page">
        <div className="panel">
          <AppHeader
            subtitle="Tournament Complete"
            topRight={<span className="badge">🏆 FINAL</span>}
            middleItems={['🏆 Tournament complete', 'Best of 3 · Single elimination']}
            bottomLeft={won ? 'You won the tournament!' : `${tournament.winnerName} wins the tournament!`}
          />
          <div className="tournament-body">
            <h2 className="winner-title">
              {won ? 'You won the tournament! 🏆' : `${tournament.winnerName} wins the tournament! 🏆`}
            </h2>
            {!won && myPlacementLabel && <p className="status">You finished in {myPlacementLabel}.</p>}
            {myResult && (
              <p className="status points-breakdown">
                {tierLabelForPlacement(myResult.placement)}: +{myResult.pointsAwarded} pts
              </p>
            )}
            {myProfile?.currentStreak > 1 && (
              <p className="status streak-callout">🔥 {myProfile.currentStreak} tournament win streak!</p>
            )}
            {!!me && (
              <button className="btn-ghost btn-ghost-danger" onClick={handleForfeit}>
                🏳️ Return to Lobby
              </button>
            )}
            <BracketView matches={matches} />
          </div>
          {connectionError && <p className="error">{connectionError}</p>}
        </div>
        {showUpgradePrompt && (
          <AuthModal
            variant="post-tournament"
            won={won}
            myResult={myResult}
            defaultUsername={me?.name}
            onClose={() => setPromptDismissed(true)}
            onAuthenticated={() => setPromptDismissed(true)}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <div className="page">
        <div className="panel">
          <AppHeader
            subtitle={roundLabel(tournament.currentRound, totalRounds)}
            topRight={
              <span className="badge">
                ROUND {tournament.currentRound} / {totalRounds}
              </span>
            }
            middleItems={[
              '🏆 Tournament · Bracket in progress',
              'Best of 3 · Loser eliminated',
              `${playersRemaining} players remaining`,
            ]}
            bottomLeft="⚡ Waiting for the next round"
          />
          <div className="tournament-body">
            {myMatch && myMatch.status === 'complete' && myMatch.winnerId === me.id && (
              <p className="status">You won this round! Waiting for other matches to finish...</p>
            )}

            {myMatch && myMatch.status === 'complete' && myMatch.winnerId !== me.id && (
              <p className="status">
                You were eliminated. Thanks for playing!
                {myPlacementLabel && ` You finished in ${myPlacementLabel}.`}
              </p>
            )}

            {!myMatch && (
              <p className="status">
                {me
                  ? `You were eliminated. Thanks for playing!${myPlacementLabel ? ` You finished in ${myPlacementLabel}.` : ''}`
                  : 'Spectating — tournament already in progress.'}
              </p>
            )}

            {!!me && (
              <button className="btn-ghost btn-ghost-danger" onClick={handleForfeit}>
                🏳️ Return to Lobby
              </button>
            )}

            <BracketView matches={matches} />
          </div>
          {connectionError && <p className="error">{connectionError}</p>}
        </div>
        {showUpgradePrompt && (
          <AuthModal
            variant="post-tournament"
            won={won}
            myResult={myResult}
            defaultUsername={me?.name}
            onClose={() => setPromptDismissed(true)}
            onAuthenticated={() => setPromptDismissed(true)}
          />
        )}
      </div>
      {roundModal}
    </>
  );
}

export default Tournament;
